import React, { useState } from 'react';
import {
  Card, DatePicker, Button, Table, Statistic, Row, Col, Tag, Space,
  Typography, Alert, Spin, message, Tooltip, Collapse
} from 'antd';
import {
  ThunderboltOutlined, SwapRightOutlined, CheckOutlined,
  CloseOutlined, CarOutlined, TeamOutlined, CalendarOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { analyzeOptimizer, applyOptimizerSuggestion } from '../../services/adminService';

const { Title, Text } = Typography;

const OptimizerPage = () => {
  const [date, setDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState({});
  const [result, setResult] = useState(null);
  const [dismissed, setDismissed] = useState([]);

  const handleAnalyze = async () => {
    if (!date) return;
    setLoading(true);
    setDismissed([]);
    try {
      const res = await analyzeOptimizer(date.format('YYYY-MM-DD'));
      if (res.success) {
        setResult(res.data);
      } else {
        message.error(res.error || 'Erreur lors de l\'analyse');
      }
    } catch (err) {
      message.error('Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (suggestion) => {
    setApplying(prev => ({ ...prev, [suggestion.id]: true }));
    try {
      const res = await applyOptimizerSuggestion({
        leadId: suggestion.leadId,
        toTeam: suggestion.toTeam,
        newHeure: suggestion.newHeure,
        date: date.format('YYYY-MM-DD')
      });
      if (res.success) {
        message.success(`RDV réaffecté à ${suggestion.toTeam}`);
        // Re-analyze to refresh
        handleAnalyze();
      } else {
        message.error(res.error || 'Erreur lors de l\'application');
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Erreur lors de l\'application');
    } finally {
      setApplying(prev => ({ ...prev, [suggestion.id]: false }));
    }
  };

  const handleDismiss = (suggestionId) => {
    setDismissed(prev => [...prev, suggestionId]);
  };

  const activeSuggestions = result?.suggestions?.filter(s => !dismissed.includes(s.id)) || [];

  const teamColumns = [
    {
      title: 'Équipe',
      dataIndex: 'team',
      key: 'team',
      render: (text) => <Text strong>{text}</Text>,
      width: 160
    },
    {
      title: 'Dépôt',
      key: 'depot',
      render: (_, record) => (
        <Text type="secondary">{record.depot} ({record.depotVille})</Text>
      ),
      width: 200
    },
    {
      title: 'RDV',
      key: 'rdvs',
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {record.rdvs.map((rdv, i) => (
            <Tag key={i} color="blue">
              {rdv.heure} - {rdv.cp} ({rdv.surface}m²)
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Capacité',
      key: 'capacity',
      render: (_, record) => {
        const ratio = record.rdvCount / record.capacity;
        const color = ratio >= 1 ? 'red' : ratio >= 0.66 ? 'orange' : 'green';
        return <Tag color={color}>{record.rdvCount}/{record.capacity}</Tag>;
      },
      width: 100,
      align: 'center'
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          <ThunderboltOutlined style={{ marginRight: 8 }} />
          Optimiseur de RDV
        </Title>
        <Text type="secondary">
          Analysez les RDV du jour et optimisez les affectations entre équipes
        </Text>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space size="middle">
          <DatePicker
            value={date}
            onChange={setDate}
            format="DD/MM/YYYY"
            style={{ width: 200 }}
            disabledDate={(d) => d && d.day() === 0}
          />
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={handleAnalyze}
            loading={loading}
            size="large"
          >
            Analyser la journée
          </Button>
        </Space>
      </Card>

      {loading && (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Analyse en cours... Calcul des distances et optimisations</Text>
          </div>
        </div>
      )}

      {result && !loading && (
        <>
          {/* KPIs */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Total RDV"
                  value={result.totalRdv}
                  prefix={<CalendarOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Équipes actives"
                  value={result.activeTeams}
                  prefix={<TeamOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Suggestions"
                  value={activeSuggestions.length}
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: activeSuggestions.length > 0 ? '#fa8c16' : '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Gain potentiel"
                  value={result.totalPotentialSavingKm}
                  suffix="km"
                  prefix={<CarOutlined />}
                  valueStyle={{ color: result.totalPotentialSavingKm > 0 ? '#52c41a' : undefined }}
                />
              </Card>
            </Col>
          </Row>

          {/* Suggestions */}
          {activeSuggestions.length > 0 ? (
            <Card
              title={<><ThunderboltOutlined /> Suggestions d'optimisation</>}
              style={{ marginBottom: 16 }}
            >
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {activeSuggestions.map((sug) => (
                  <Card
                    key={sug.id}
                    size="small"
                    style={{ border: '1px solid #ffd591', background: '#fff7e6' }}
                  >
                    <Row gutter={16} align="middle">
                      <Col xs={24} md={10}>
                        <Space direction="vertical" size={2}>
                          <Text strong>{sug.clientName || `Lead #${sug.leadId}`}</Text>
                          <Text type="secondary">
                            <EnvironmentOutlined /> {sug.address}
                          </Text>
                          <Space>
                            <Tag>{sug.surface}m²</Tag>
                            <Tag color="blue">{sug.currentHeure}</Tag>
                            <Tag>{sug.cp}</Tag>
                          </Space>
                        </Space>
                      </Col>
                      <Col xs={24} md={8}>
                        <div style={{ textAlign: 'center' }}>
                          <Space align="center" size={8}>
                            <Tag color="red" style={{ fontSize: 13, padding: '2px 8px' }}>
                              {sug.fromTeam}
                            </Tag>
                            <SwapRightOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                            <Tag color="green" style={{ fontSize: 13, padding: '2px 8px' }}>
                              {sug.toTeam}
                            </Tag>
                          </Space>
                          {sug.newHeure !== sug.currentHeure && (
                            <div style={{ marginTop: 4 }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Nouveau créneau : <Text strong>{sug.newHeure}</Text>
                              </Text>
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col xs={24} md={6} style={{ textAlign: 'right' }}>
                        <div style={{ marginBottom: 8 }}>
                          <Tooltip title={`Route avant: ${sug.score.totalBefore}km → après: ${sug.score.totalAfter}km`}>
                            <Tag color="green" style={{ fontSize: 14, padding: '4px 12px' }}>
                              <CarOutlined /> -{sug.score.savedKm} km
                            </Tag>
                          </Tooltip>
                        </div>
                        <Space>
                          <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={() => handleApply(sug)}
                            loading={applying[sug.id]}
                            size="small"
                          >
                            Accepter
                          </Button>
                          <Button
                            icon={<CloseOutlined />}
                            onClick={() => handleDismiss(sug.id)}
                            size="small"
                          >
                            Rejeter
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Raison : {sug.reasonDetail}
                      </Text>
                    </div>
                  </Card>
                ))}
              </Space>
            </Card>
          ) : (
            <Alert
              type="success"
              message="Aucune optimisation nécessaire"
              description="Les RDV sont déjà bien répartis entre les équipes pour cette journée !"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Team summary */}
          <Collapse
            defaultActiveKey={['teams']}
            items={[{
              key: 'teams',
              label: <><TeamOutlined /> Vue des équipes du jour ({result.teamSummaries?.length || 0})</ >,
              children: (
                <Table
                  dataSource={result.teamSummaries}
                  columns={teamColumns}
                  rowKey="team"
                  pagination={false}
                  size="small"
                />
              )
            }]}
          />
        </>
      )}

      {!result && !loading && (
        <Alert
          type="info"
          message="Sélectionnez une date et cliquez sur « Analyser la journée »"
          description="L'optimiseur va analyser tous les RDV, détecter les affectations sous-optimales et proposer des réaffectations pour réduire les distances parcourues."
          showIcon
        />
      )}
    </div>
  );
};

export default OptimizerPage;
